import type Keycloak from 'keycloak-js';
import { Capacitor } from '@capacitor/core';

import { getKeycloak } from './keycloak';
import { buildKeycloakEndSessionUrl } from './keycloakEndSession';
import { getKeycloakRedirectUri } from './keycloakRedirect';
import { logoutWithKeycloakNative } from './keycloakNativeAuth';
import { isOtpApiNativeAuthFlow } from './nativeAuthFlow';
import { tryRevokeKeycloakRefreshToken } from './keycloakTokenRevoke';
import { clearKeycloakSessionStorage } from './keycloakTokenRefresh';

export type KeycloakLogoutOptions = {
	redirectUri?: string;
	/**
	 * When false, only clears local tokens without revoking refresh/offline sessions
	 * or redirecting to Keycloak end-session.
	 * Use when the server session is already dead (e.g. invalid_grant on refresh).
	 */
	endSsoSession?: boolean;
};

export type KeycloakLogoutResult = 'redirected' | 'local';

/** Clears in-memory Keycloak tokens so refresh cannot repopulate hub auth state. */
export function clearKeycloakInMemorySession(keycloak: Keycloak = getKeycloak()): void {
	keycloak.authenticated = false;
	keycloak.token = undefined;
	keycloak.refreshToken = undefined;
	keycloak.idToken = undefined;
	keycloak.tokenParsed = undefined;
	keycloak.refreshTokenParsed = undefined;
	keycloak.idTokenParsed = undefined;
	keycloak.subject = undefined;
	keycloak.sessionId = undefined;
}

/**
 * Clears persisted and in-memory Keycloak state, then optionally ends the server session.
 * Local state is always cleared first so the app is logged out even when revoke / redirect fails.
 *
 * - Always best-effort revokes the refresh/offline token when `endSsoSession` is true
 * - OAuth flow: also redirects to Keycloak end-session (browser cookie SSO)
 * - OTP API flow: revoke only (no browser SSO cookies were set)
 */
export async function performKeycloakLogout(
	options: KeycloakLogoutOptions = {},
): Promise<KeycloakLogoutResult> {
	const redirectUri = options.redirectUri ?? getKeycloakRedirectUri();
	const endSsoSession = options.endSsoSession ?? true;
	const keycloak = getKeycloak();
	const refreshToken = keycloak.refreshToken;
	const hadActiveSession = Boolean(keycloak.authenticated || refreshToken);

	clearKeycloakSessionStorage();
	clearKeycloakInMemorySession(keycloak);

	if (!endSsoSession) {
		return 'local';
	}

	await tryRevokeKeycloakRefreshToken(refreshToken);

	if (!hadActiveSession) {
		return 'local';
	}

	// OTP API login never sets browser SSO cookies — revoke is the server-side logout.
	if (isOtpApiNativeAuthFlow()) {
		return 'local';
	}

	try {
		if (Capacitor.isNativePlatform()) {
			await logoutWithKeycloakNative(redirectUri);

			return 'redirected';
		}

		window.location.assign(buildKeycloakEndSessionUrl(redirectUri));

		return 'redirected';
	} catch {
		return 'local';
	}
}
